/**
 * Custom hook for shipping page data management
 * Handles: data loading, CRUD, rate computation, Excel import/export
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import type {
  ShippingCarrier,
  CarrierRouteRates,
  RouteShippingConfig,
  ShippingRouteId,
  ComputedShippingRate,
  CustomShippingRoute,
} from '../../types/shipping';
import { DESI_BREAKPOINTS, SHIPPING_ROUTES } from '../../types/shipping';
import {
  getAllShippingCarriers,
  saveShippingCarrier,
  updateShippingCarrier,
  deleteShippingCarrier,
  getAllCarrierRouteRates,
  upsertCarrierRouteRates,
  getAllRouteShippingConfigs,
  upsertRouteShippingConfig,
  initializeDefaultShippingCarriers,
  getAllCustomShippingRoutes,
  saveCustomShippingRoute,
  deleteCustomShippingRoute,
  initializeDefaultShippingRoutes,
} from '../../utils/database';
import { apiGetAllExchangeRates } from '../../services/api';
import * as XLSX from 'xlsx';
import { logger } from '../../utils/logger';

// DESI display logic: 0.5-10 by 0.5, 10-15 by 1
const DEFAULT_VISIBLE_DESI = [
  0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4, 4.5, 5,
  5.5, 6, 6.5, 7, 7.5, 8, 8.5, 9, 9.5, 10,
  11, 12, 13, 14, 15
];

export function useShippingData() {
  // Core data
  const [carriers, setCarriers] = useState<ShippingCarrier[]>([]);
  const [allRates, setAllRates] = useState<CarrierRouteRates[]>([]);
  const [routeConfigs, setRouteConfigs] = useState<RouteShippingConfig[]>([]);
  const [routes, setRoutes] = useState<CustomShippingRoute[]>([]);
  const [exchangeRates, setExchangeRates] = useState<Record<string, number>>({});

  // UI state
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [expandedCarriers, setExpandedCarriers] = useState<Set<string>>(new Set());
  const [hasChanges, setHasChanges] = useState(false);
  const [showAllDesi, setShowAllDesi] = useState(false);
  const [visibleDesi, setVisibleDesi] = useState<number[]>(DEFAULT_VISIBLE_DESI);
  const [importStatus, setImportStatus] = useState<{ success: number; errors: number } | null>(null);

  // Modal state
  const [showAddCarrier, setShowAddCarrier] = useState(false);
  const [newCarrier, setNewCarrier] = useState({ name: '', shortName: '', color: '#6366f1', currency: 'USD' });
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [showAddRoute, setShowAddRoute] = useState(false);
  const [newRoute, setNewRoute] = useState({
    name: '', shortName: '', origin: 'TR', destination: '',
    flag: '', defaultCurrency: 'USD', currencySymbol: '$'
  });
  const [showCarrierRoutes, setShowCarrierRoutes] = useState<string | null>(null);
  const [deleteRouteConfirm, setDeleteRouteConfirm] = useState<string | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // Convert static SHIPPING_ROUTES to CustomShippingRoute format (fallback)
  const getDefaultRoutes = (): CustomShippingRoute[] => {
    return SHIPPING_ROUTES.map(r => ({
      id: r.id,
      routeId: r.id,
      name: r.name,
      shortName: r.shortName,
      origin: r.origin,
      destination: r.destination,
      flag: r.flag,
      defaultCurrency: r.defaultCurrency,
      currencySymbol: r.currencySymbol,
      isActive: true,
      isDefault: true,
      createdAt: new Date(),
      updatedAt: new Date(),
    }));
  };

  // Load all data
  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      await initializeDefaultShippingCarriers();
      await initializeDefaultShippingRoutes();
      const [carriersData, ratesData, configsData, routesData, exchangeRatesData] = await Promise.all([
        getAllShippingCarriers(),
        getAllCarrierRouteRates(),
        getAllRouteShippingConfigs(),
        getAllCustomShippingRoutes(),
        apiGetAllExchangeRates(),
      ]);
      setCarriers(carriersData);
      setAllRates(ratesData);
      setRouteConfigs(configsData);

      const ratesLookup: Record<string, number> = {};
      exchangeRatesData.forEach(r => {
        ratesLookup[r.currency_code] = r.rate_to_usd;
      });
      ratesLookup['USD'] = 1;
      setExchangeRates(ratesLookup);

      const activeRoutes = routesData.filter(r => r.isActive);
      setRoutes(activeRoutes.length > 0 ? activeRoutes : getDefaultRoutes());

      if (carriersData.length > 0) {
        setExpandedCarriers(prev => prev.size === 0 ? new Set([carriersData[0].id]) : prev);
      }
    } catch (error) {
      logger.error('Failed to load shipping data:', error);
      setRoutes(getDefaultRoutes());
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Toggle DESI visibility
  const toggleDesiView = () => {
    if (showAllDesi) {
      setVisibleDesi(DEFAULT_VISIBLE_DESI);
    } else {
      setVisibleDesi([...DESI_BREAKPOINTS]);
    }
    setShowAllDesi(!showAllDesi);
  };

  // Toggle carrier expansion
  const toggleCarrier = (carrierId: string) => {
    setExpandedCarriers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(carrierId)) {
        newSet.delete(carrierId);
      } else {
        newSet.add(carrierId);
      }
      return newSet;
    });
  };

  // Get routes enabled for a specific carrier
  const getCarrierRoutes = (carrier: ShippingCarrier): CustomShippingRoute[] => {
    if (!carrier.enabledRoutes || carrier.enabledRoutes.length === 0) {
      return routes;
    }
    return routes.filter(r => carrier.enabledRoutes!.includes(r.routeId));
  };

  // Get rate for a specific carrier/route/desi
  const getRate = (carrierId: string, routeId: string, desi: number): number | undefined => {
    const carrierRates = allRates.find(r => r.carrierId === carrierId && r.routeId === routeId);
    return carrierRates?.rates[desi];
  };

  // Find nearest upper rate for a given desi (interpolation for missing values)
  const findNearestUpperRate = (rates: Record<number, number>, desi: number): { rate: number; usedDesi: number } | null => {
    const sortedDesiValues = Object.keys(rates)
      .map(Number)
      .filter(d => rates[d] > 0)
      .sort((a, b) => a - b);

    if (sortedDesiValues.length === 0) return null;

    for (const d of sortedDesiValues) {
      if (d >= desi && rates[d] > 0) {
        return { rate: rates[d], usedDesi: d };
      }
    }

    return null;
  };

  // Get computed rate (cheapest or priority) for a route/desi
  const getComputedRate = (routeId: string, desi: number): ComputedShippingRate | null => {
    const config = routeConfigs.find(c => c.routeId === routeId);
    const routeRates = allRates.filter(r => r.routeId === routeId);

    if (routeRates.length === 0) return null;

    if (config?.selectionMode === 'priority' && config.priorityCarrierId) {
      const priorityRates = routeRates.find(r => r.carrierId === config.priorityCarrierId);
      const carrier = carriers.find(c => c.id === config.priorityCarrierId);
      if (priorityRates && carrier) {
        const nearestRate = findNearestUpperRate(priorityRates.rates, desi);
        if (nearestRate) {
          return {
            routeId: routeId as ShippingRouteId,
            desi,
            carrierId: carrier.id,
            carrierName: carrier.shortName,
            rate: nearestRate.rate,
            currency: carrier.currency || priorityRates.currency,
            selectionReason: 'priority',
          };
        }
      }
    }

    let cheapest: ComputedShippingRate | null = null;
    for (const rates of routeRates) {
      const carrier = carriers.find(c => c.id === rates.carrierId);
      if (!carrier) continue;

      const nearestRate = findNearestUpperRate(rates.rates, desi);
      if (nearestRate) {
        if (cheapest === null || nearestRate.rate < cheapest.rate) {
          cheapest = {
            routeId: routeId as ShippingRouteId,
            desi,
            carrierId: carrier.id,
            carrierName: carrier.shortName,
            rate: nearestRate.rate,
            currency: carrier.currency || rates.currency,
            selectionReason: routeRates.length === 1 ? 'only_option' : 'cheapest',
          };
        }
      }
    }

    return cheapest;
  };

  // Update rate for a carrier/route/desi
  const updateRate = (carrierId: string, routeId: string, desi: number, value: number | null) => {
    setAllRates(prev => {
      const existing = prev.find(r => r.carrierId === carrierId && r.routeId === routeId);
      if (existing) {
        const newRates = { ...existing.rates };
        if (value === null) {
          delete newRates[desi];
        } else {
          newRates[desi] = value;
        }
        return prev.map(r =>
          r.carrierId === carrierId && r.routeId === routeId
            ? { ...r, rates: newRates }
            : r
        );
      } else if (value !== null) {
        const route = routes.find(r => r.routeId === routeId);
        const newRate: CarrierRouteRates = {
          id: `temp-${Date.now()}`,
          carrierId,
          routeId: routeId as ShippingRouteId,
          currency: route?.defaultCurrency || 'USD',
          rates: { [desi]: value },
          createdAt: new Date(),
          updatedAt: new Date(),
        };
        return [...prev, newRate];
      }
      return prev;
    });
    setHasChanges(true);
  };

  // Save all changes to database
  const saveAllChanges = async () => {
    setIsSaving(true);
    try {
      for (const rates of allRates) {
        await upsertCarrierRouteRates({
          carrierId: rates.carrierId,
          routeId: rates.routeId,
          currency: rates.currency,
          rates: rates.rates,
        });
      }
      for (const config of routeConfigs) {
        await upsertRouteShippingConfig({
          routeId: config.routeId,
          selectionMode: config.selectionMode,
          priorityCarrierId: config.priorityCarrierId,
        });
      }
      setHasChanges(false);
      setImportStatus({ success: allRates.length, errors: 0 });
      setTimeout(() => setImportStatus(null), 2000);
    } catch (error) {
      logger.error('Failed to save:', error);
      setImportStatus({ success: 0, errors: 1 });
      setTimeout(() => setImportStatus(null), 2000);
    } finally {
      setIsSaving(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    for (const carrier of carriers.filter(c => c.isActive)) {
      const carrierRates = allRates.filter(r => r.carrierId === carrier.id);
      const cRoutes = getCarrierRoutes(carrier);

      const headers: (string | number)[] = ['DESI', ...cRoutes.map(r => r.shortName)];
      const data: (string | number)[][] = [headers];

      for (const desi of DESI_BREAKPOINTS) {
        const row: (number | string)[] = [desi];
        for (const route of cRoutes) {
          const rates = carrierRates.find(r => r.routeId === route.routeId);
          row.push(rates?.rates[desi] ?? '');
        }
        data.push(row);
      }

      const ws = XLSX.utils.aoa_to_sheet(data);
      XLSX.utils.book_append_sheet(wb, ws, carrier.shortName);
    }

    XLSX.writeFile(wb, `shipping-rates-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  // Import from Excel
  const importFromExcel = async (file: File) => {
    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer);

      let successCount = 0;
      let errorCount = 0;

      for (const sheetName of wb.SheetNames) {
        const carrier = carriers.find(c => c.shortName === sheetName || c.name === sheetName);
        if (!carrier) {
          errorCount++;
          continue;
        }

        const ws = wb.Sheets[sheetName];
        const data = XLSX.utils.sheet_to_json(ws, { header: 1 }) as unknown as (string | number)[][];

        if (data.length < 2) continue;

        const headers = data[0].slice(1).map(h => {
          const str = String(h);
          const route = routes.find(r => r.shortName === str || r.routeId === str);
          return route?.routeId || str;
        });

        for (let i = 1; i < data.length; i++) {
          const row = data[i];
          const desi = parseFloat(String(row[0]));
          if (isNaN(desi)) continue;

          for (let j = 0; j < headers.length; j++) {
            const routeId = headers[j];
            const value = row[j + 1];

            if (value !== '' && value !== undefined && value !== null) {
              const numValue = parseFloat(String(value));
              if (!isNaN(numValue)) {
                updateRate(carrier.id, routeId, desi, numValue);
                successCount++;
              }
            }
          }
        }
      }

      setImportStatus({ success: successCount, errors: errorCount });
      setTimeout(() => setImportStatus(null), 3000);
    } catch (error) {
      logger.error('Excel import failed:', error);
      setImportStatus({ success: 0, errors: 1 });
      setTimeout(() => setImportStatus(null), 3000);
    }
  };

  // Handle file input
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      importFromExcel(file);
      e.target.value = '';
    }
  };

  // Set priority carrier for a route
  const setPriorityCarrier = (routeId: string, carrierId: string | null) => {
    setRouteConfigs(prev => {
      const existing = prev.find(c => c.routeId === routeId);
      if (existing) {
        return prev.map(c =>
          c.routeId === routeId
            ? {
                ...c,
                selectionMode: carrierId ? 'priority' : 'cheapest',
                priorityCarrierId: carrierId || undefined,
              } as RouteShippingConfig
            : c
        );
      } else {
        return [...prev, {
          id: `temp-${Date.now()}`,
          routeId: routeId as ShippingRouteId,
          selectionMode: carrierId ? 'priority' : 'cheapest',
          priorityCarrierId: carrierId || undefined,
          createdAt: new Date(),
          updatedAt: new Date(),
        } as RouteShippingConfig];
      }
    });
    setHasChanges(true);
  };

  // Add new carrier
  const handleAddCarrier = async () => {
    if (!newCarrier.name || !newCarrier.shortName) return;

    try {
      const carrier = await saveShippingCarrier({
        name: newCarrier.name,
        shortName: newCarrier.shortName,
        color: newCarrier.color,
        currency: newCarrier.currency,
        isActive: true,
      });
      setCarriers(prev => [...prev, carrier]);
      setShowAddCarrier(false);
      setNewCarrier({ name: '', shortName: '', color: '#6366f1', currency: 'USD' });
      setExpandedCarriers(prev => new Set([...Array.from(prev), carrier.id]));
    } catch (error) {
      logger.error('Failed to add carrier:', error);
    }
  };

  // Delete carrier
  const handleDeleteCarrier = async (carrierId: string) => {
    try {
      await deleteShippingCarrier(carrierId);
      setCarriers(prev => prev.filter(c => c.id !== carrierId));
      setAllRates(prev => prev.filter(r => r.carrierId !== carrierId));
      setDeleteConfirm(null);
    } catch (error) {
      logger.error('Failed to delete carrier:', error);
    }
  };

  // Add new route
  const handleAddRoute = async () => {
    if (!newRoute.name || !newRoute.shortName || !newRoute.destination) return;

    try {
      const routeId = `${newRoute.origin}_${newRoute.destination}`.toUpperCase();
      const route = await saveCustomShippingRoute({
        routeId,
        name: newRoute.name,
        shortName: newRoute.shortName,
        origin: newRoute.origin.toUpperCase(),
        destination: newRoute.destination.toUpperCase(),
        flag: newRoute.flag || '',
        defaultCurrency: newRoute.defaultCurrency,
        currencySymbol: newRoute.currencySymbol,
        isActive: true,
        isDefault: false,
      });
      setRoutes(prev => [...prev, route]);
      setShowAddRoute(false);
      setNewRoute({
        name: '', shortName: '', origin: 'TR', destination: '',
        flag: '', defaultCurrency: 'USD', currencySymbol: '$'
      });
    } catch (error) {
      logger.error('Failed to add route:', error);
    }
  };

  // Delete route
  const handleDeleteRoute = async (routeId: string) => {
    try {
      const route = routes.find(r => r.routeId === routeId);
      if (route) {
        await deleteCustomShippingRoute(route.id);
        setRoutes(prev => prev.filter(r => r.routeId !== routeId));
        setAllRates(prev => prev.filter(r => r.routeId !== routeId));
      }
      setDeleteRouteConfirm(null);
    } catch (error) {
      logger.error('Failed to delete route:', error);
    }
  };

  // Toggle route for carrier
  const toggleCarrierRoute = async (carrier: ShippingCarrier, routeId: string) => {
    const currentEnabled = carrier.enabledRoutes || routes.map(r => r.routeId);
    let newEnabled: string[];

    if (currentEnabled.includes(routeId)) {
      newEnabled = currentEnabled.filter(id => id !== routeId);
      if (newEnabled.length === 0) return;
    } else {
      newEnabled = [...currentEnabled, routeId];
    }

    const updatedCarrier = { ...carrier, enabledRoutes: newEnabled };
    try {
      await updateShippingCarrier(updatedCarrier);
      setCarriers(prev => prev.map(c => c.id === carrier.id ? updatedCarrier : c));
      setHasChanges(true);
    } catch (error) {
      logger.error('Failed to update carrier routes:', error);
    }
  };

  // Update carrier currency
  const updateCarrierCurrency = async (carrier: ShippingCarrier, newCurrency: string) => {
    try {
      const updated = await updateShippingCarrier({ ...carrier, currency: newCurrency });
      setCarriers(prev => prev.map(c => c.id === carrier.id ? updated : c));
      setHasChanges(true);
    } catch (error) {
      logger.error('Failed to update carrier currency:', error);
    }
  };

  // Check if carrier is priority for route
  const isPriorityCarrier = (routeId: string, carrierId: string): boolean => {
    const config = routeConfigs.find(c => c.routeId === routeId);
    return config?.selectionMode === 'priority' && config.priorityCarrierId === carrierId;
  };

  return {
    // Core data
    carriers, routes, exchangeRates,
    // UI state
    isLoading, isSaving, hasChanges, showAllDesi, visibleDesi,
    expandedCarriers, importStatus,
    // Modal state + setters
    showAddCarrier, setShowAddCarrier,
    newCarrier, setNewCarrier,
    deleteConfirm, setDeleteConfirm,
    showAddRoute, setShowAddRoute,
    newRoute, setNewRoute,
    showCarrierRoutes, setShowCarrierRoutes,
    deleteRouteConfirm, setDeleteRouteConfirm,
    // File ref
    fileInputRef, handleFileChange,
    // Actions
    toggleDesiView, toggleCarrier, saveAllChanges,
    exportToExcel,
    handleAddCarrier, handleDeleteCarrier,
    handleAddRoute, handleDeleteRoute,
    toggleCarrierRoute, updateCarrierCurrency,
    setPriorityCarrier, updateRate,
    // Getters
    getCarrierRoutes, getRate, getComputedRate, isPriorityCarrier,
  };
}
